import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import { CsvAsStringArrays } from '@malou-io/package-utils';

import { CategoryPerformanceSummaryCsvData } from '#modules/statistics-e-reputation/components/aggregated-e-reputation/semantic-analysis-v2/models/category-performance-summary';
import { AggregatedSemanticAnalysisExportService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-semantic-analysis-export.service';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';

type Data = CategoryPerformanceSummaryCsvData[] | null;

@Injectable()
export class AggregatedSemanticAnalysisCategoryPerformanceInsightsService extends AbstractCsvService<Data> {
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);
    private readonly _aggregatedSemanticAnalysisExportService = inject(AggregatedSemanticAnalysisExportService);
    private readonly _categoryPerformanceSummaryData$ = toObservable(
        this._aggregatedStatisticsStore.semanticAnalysisV2CategoryPerformanceSummaryData
    );

    protected _getData$(): Observable<Data> {
        return this._aggregatedSemanticAnalysisExportService.waitForData$(this._categoryPerformanceSummaryData$);
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data;
    }

    protected override _getCsvHeaderRow(): string[] {
        return [
            'Restaurant Name',
            '# Negative mentions',
            'Evolution negative mentions',
            '# Positive mentions',
            'Evolution positive mentions',
            '% Negative mentions - Overall',
            '% Positive mentions - Overall',
            '% Negative mentions - Food',
            '% Positive mentions - Food',
            '% Negative mentions - Service',
            '% Positive mentions - Service',
            '% Negative mentions - Ambiance',
            '% Positive mentions - Ambiance',
            '% Negative mentions - Price',
            '% Positive mentions - Price',
            '% Negative mentions - Hygiene',
            '% Positive mentions - Hygiene',
        ];
    }

    protected override _getCsvDataRows(data: CategoryPerformanceSummaryCsvData[]): CsvAsStringArrays {
        return data.map((item) => {
            const restaurantName = item.restaurantName;
            const negativeMentions = item.allCategories.negativeMentions.toString();
            const negativeMentionsEvolution = item.allCategories.negativeMentionsEvolution?.toString() ?? '-';
            const positiveMentions = item.allCategories.positiveMentions.toString();
            const positiveMentionsEvolution = item.allCategories.positiveMentionsEvolution?.toString() ?? '-';
            const negativeMentionsOverallPercentage = `${item.allCategories.negativeMentionsPercentage ?? '-'}%`;
            const positiveMentionsOverallPercentage = `${item.allCategories.positiveMentionsPercentage ?? '-'}%`;
            const negativeMentionsFoodPercentage = `${item.food.negativeMentionsPercentage ?? '-'}%`;
            const positiveMentionsFoodPercentage = `${item.food.positiveMentionsPercentage ?? '-'}%`;
            const negativeMentionsServicePercentage = `${item.service.negativeMentionsPercentage ?? '-'}%`;
            const positiveMentionsServicePercentage = `${item.service.positiveMentionsPercentage ?? '-'}%`;
            const negativeMentionsAmbiancePercentage = `${item.atmosphere.negativeMentionsPercentage ?? '-'}%`;
            const positiveMentionsAmbiancePercentage = `${item.atmosphere.positiveMentionsPercentage ?? '-'}%`;
            const negativeMentionsPricePercentage = `${item.price.negativeMentionsPercentage ?? '-'}%`;
            const positiveMentionsPricePercentage = `${item.price.positiveMentionsPercentage ?? '-'}%`;
            const negativeMentionsHygienePercentage = `${item.hygiene.negativeMentionsPercentage ?? '-'}%`;
            const positiveMentionsHygienePercentage = `${item.hygiene.positiveMentionsPercentage ?? '-'}%`;

            return [
                restaurantName,
                negativeMentions,
                negativeMentionsEvolution,
                positiveMentions,
                positiveMentionsEvolution,
                negativeMentionsOverallPercentage,
                positiveMentionsOverallPercentage,
                negativeMentionsFoodPercentage,
                positiveMentionsFoodPercentage,
                negativeMentionsServicePercentage,
                positiveMentionsServicePercentage,
                negativeMentionsAmbiancePercentage,
                positiveMentionsAmbiancePercentage,
                negativeMentionsPricePercentage,
                positiveMentionsPricePercentage,
                negativeMentionsHygienePercentage,
                positiveMentionsHygienePercentage,
            ];
        });
    }
}
